export interface CurrencyMeta {
  code: string
  /** ISO 4217 minor units (number of decimal places). */
  minorUnits: number
  /** ISO 3166-1 alpha-2 region used for the flag ("EU" for the euro). */
  region: string
}

/** ISO 4217 minor units that differ from the common default of 2. */
const MINOR_UNIT_EXCEPTIONS: Record<string, number> = {
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
}

export function getMinorUnits(code: string): number {
  return MINOR_UNIT_EXCEPTIONS[code.toUpperCase()] ?? 2
}

/**
 * Currencies supported in the UI for now. The final list will follow the
 * coverage of the approved rate provider (decision D4).
 */
const CURRENCY_REGIONS: Record<string, string> = {
  USD: 'US',
  EUR: 'EU',
  GBP: 'GB',
  JPY: 'JP',
  CHF: 'CH',
  CAD: 'CA',
  AUD: 'AU',
  NZD: 'NZ',
  CNY: 'CN',
  HKD: 'HK',
  SGD: 'SG',
  INR: 'IN',
  KRW: 'KR',
  TRY: 'TR',
  RUB: 'RU',
  BRL: 'BR',
  MXN: 'MX',
  ZAR: 'ZA',
  SEK: 'SE',
  NOK: 'NO',
  DKK: 'DK',
  PLN: 'PL',
  SAR: 'SA',
  AED: 'AE',
  QAR: 'QA',
  KWD: 'KW',
  BHD: 'BH',
  OMR: 'OM',
  JOD: 'JO',
  EGP: 'EG',
  MAD: 'MA',
  TND: 'TN',
  DZD: 'DZ',
  IQD: 'IQ',
  LYD: 'LY',
  YER: 'YE',
  PKR: 'PK',
  IDR: 'ID',
  MYR: 'MY',
  THB: 'TH',
}

export const CURRENCIES: readonly CurrencyMeta[] = Object.entries(CURRENCY_REGIONS).map(
  ([code, region]) => ({ code, region, minorUnits: getMinorUnits(code) }),
)

export const POPULAR_CURRENCIES = ['USD', 'EUR', 'GBP', 'SAR', 'AED', 'TRY', 'JPY', 'CNY'] as const

const BY_CODE = new Map(CURRENCIES.map((c) => [c.code, c]))

export function getCurrency(code: string): CurrencyMeta | undefined {
  return BY_CODE.get(code.toUpperCase())
}
